import EditorPreviewer from 'components/EditorPreviewer';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import useMustacheSticker2Url from 'hooks/business/useMustacheSticker2Url';
import { useCallback } from 'react';
import { Descendant } from 'slate';
import { ScenarioResult } from 'views/Setting/AutoEmit/components/Result';
import { getResultContexts, ResultText } from '../BilingualExecuteContextField';
import { DescendantExecuteContext } from '../share';
import { FollowCardMessage } from 'components/Dialogue';
import { useTranslate } from '@tolgee/react';

export interface ResultProps extends UseGetResultListProps {
  value: DescendantExecuteContext;
  query?: DescendantExecuteContext;
}

export function Result(props: ResultProps) {
  const { value, query, ...otherProps } = props;

  const getResultList = useGetResultList(otherProps);

  return <ScenarioResult list={getResultList(value, query)} />;
}

export interface UseGetResultListProps {
  platform?: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  systemContent?: string;
}

export function useGetResultList(props: UseGetResultListProps) {
  const { platform, region, systemContent } = props;

  const { t } = useTranslate();

  const [getStickerUrl] = useMustacheSticker2Url(platform || false, region);

  return useCallback(
    (
      value: DescendantExecuteContext,
      query?: DescendantExecuteContext,
      supportTranslate?: boolean,
    ) => {
      const contentRender = (context: Descendant[]) => {
        const isLazadaInviteFollower =
          platform === 'LAZADA' &&
          context[0]?.type === CustomSlateNodeType.IMAGE &&
          context[0].children?.[0]?.text === 'follow';

        if (isLazadaInviteFollower) {
          return <FollowCardMessage storeName={t('marketing.店铺名称')} />;
        }

        return (
          <ResultText>
            <EditorPreviewer value={context} getStickerUrl={getStickerUrl} />
          </ResultText>
        );
      };

      const queryList = query
        ? getResultContexts(query, supportTranslate)?.map((context) => {
            return {
              type: 'query',
              content: contentRender(context),
            } as const;
          }) || []
        : [];

      const resultContext = getResultContexts(value, supportTranslate);

      const replyList = resultContext
        ? resultContext.map((context) => {
            return {
              type: 'reply',
              content: contentRender(context),
            } as const;
          })
        : [
            {
              type: 'reply',
              content: '',
            } as const,
          ];

      return [
        ...(systemContent
          ? [
              {
                type: 'system',
                content: systemContent,
              } as const,
            ]
          : []),
        ...queryList,
        ...replyList,
      ];
    },
    [getStickerUrl, systemContent, platform, t],
  );
}
